import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { MapContainer, TileLayer, useMapEvents } from "react-leaflet";
import { LeafletMouseEvent } from "leaflet";
import { sortBy } from "lodash";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import {
  changeContextPosition,
  changeShowContext,
  setMapData,
  toggleMoveEnd,
  changeLargeIconByClick,
  changeCloseContext,
} from "Redux/mapSlice";
import { throttle } from "Helper/general";
import useStations from "Hooks/useStations";
import { IStation } from "Types/station";
import { CEDARMAP_TOKEN } from "Constants/App";
import { MapirLayer } from "./MapirLayer";
import "leaflet/dist/leaflet.css";

interface IProps {
  children?: React.ReactNode;
  dragging?: boolean;
  position?: null | { lat: number; lng: number };
}

function MapHandler({ zoom }: { zoom: number }) {
  const dispatch = useAppDispatch();

  const { view, bounds, minimize } = useAppSelector((state) => state.map);
  const { zone } = useAppSelector((state) => state.zones);
  const { zone: trafficZone } = useAppSelector((state) => state.trafficZones);
  const { ids } = useAppSelector((state) => state.stations);
  const { openCircle } = useAppSelector((state) => state.tooltip);
  const { selectedCamera } = useAppSelector((state) => state.camera);
  const { page } = useAppSelector((state) => state.page);
  const { data } = useStations();

  const eventFunction: (e: LeafletMouseEvent) => void = useMemo(
    () =>
      throttle(function (event: LeafletMouseEvent) {
        const { lat, lng } = event.latlng;
        dispatch(setMapData({ mouse: { lat, lng } }));
      }, 2000),
    []
  );

  const map = useMapEvents({
    moveend(e) {
      const { lat, lng } = map.getCenter();
      dispatch(toggleMoveEnd());
      dispatch(setMapData({ center: { lat, lng }, zoom: map.getZoom() }));
    },
    mousemove(e) {
      eventFunction(e);
    },
    contextmenu(e) {
      dispatch(changeShowContext(true));
      setTimeout(() => dispatch(changeContextPosition(e.latlng)), 400);
    },
    click(e) {
      dispatch(changeLargeIconByClick(0));
    },
    dblclick(e) {
      dispatch(changeCloseContext(true));
    },
    // move(e: any) {
    //   if (e?.flyTo && e?.flyTo !== moving) {
    //     console.log("ptr");
    //     // dispatch(setMoving(e.flyTo));
    //   } else {
    //     dispatch(setMoving(false));
    //   }
    // },
  });

  useEffect(() => {
    if (map && !minimize) {
      map.setZoom(zoom);
    }
  }, [zoom]);

  // useEffect(() => {
  //   if (page === 4) {
  //     map.flyTo({ lat: 32.64674557848894, lng: 51.67777329888067 });
  //   }
  // }, [page]);

  useEffect(() => {
    if (zone === 29) {
      map.flyTo({ lat: 35.7219, lng: 51.3347 });
    } else if (zone === 7) {
      map.flyTo({ lat: 32.64674557848894, lng: 51.67777329888067 });
    }
  }, [zone]);

  useEffect(() => {
    if (trafficZone === 29) {
      map.flyTo({ lat: 35.7219, lng: 51.3347 });
    } else if (trafficZone === 7) {
      map.flyTo({ lat: 32.64674557848894, lng: 51.67777329888067 });
    }
  }, [trafficZone]);

  useEffect(() => {
    if (selectedCamera.length > 0) {
      map.flyTo({ lat: selectedCamera[0]?.GeographicalAccident, lng: selectedCamera[0]?.GeographicalLength }, 13);
    }
  }, [selectedCamera]);

  useEffect(() => {
    if (view !== null) {
      map.setView(view.center, view.zoom);
    }
  }, [view]);

  useEffect(() => {
    if (bounds !== null && bounds.positions.length > 0) {
      map.fitBounds(bounds.positions, { padding: [200, 200], ...bounds.options });
    }
  }, [bounds]);

  useEffect(() => {
    if (map && minimize) {
      const stations = data?.filter((station: IStation) => ids.includes(station.id));
      const lats = stations?.map((station: IStation) => station.lat);
      const lngs = stations?.map((station: IStation) => station.lng);
      const sortedLats = sortBy(lats);
      const sortedLngs = sortBy(lngs);

      if (sortedLats?.length > 1 && sortedLngs?.length > 1) {
        map.fitBounds(
          [
            [sortedLats[0], sortedLngs[0]],
            [sortedLats[sortedLats.length - 1], sortedLngs[sortedLngs.length - 1]],
          ],
          { padding: [30, 30] }
        );
      }
    }
  }, [minimize]);

  return <></>;
}

export default function Map({ children, dragging = true, position }: IProps) {
  const { zoom, center, minimize } = useAppSelector((state) => state.map);

  // const center = [51.505, -0.09];
  const rectangle = [
    [51.49, -0.08],
    [51.5, -0.06],
  ];

  const MAPIR_TOKEN =
    "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6IjkyMTA5YjRlNzhkM2RlYmIyNDQ1ZmFiYmUwZmY1ZGQ5NTAwZjIxNDE2MGVmZmJjYmI1ZDJmYzc5NDcxY2E3YjI0NzM1ZjY4ZWMzNzRhOTg1In0.eyJhdWQiOiI0MzEzMSIsImp0aSI6IjkyMTA5YjRlNzhkM2RlYmIyNDQ1ZmFiYmUwZmY1ZGQ5NTAwZjIxNDE2MGVmZmJjYmI1ZDJmYzc5NDcxY2E3YjI0NzM1ZjY4ZWMzNzRhOTg1IiwiaWF0IjoxNzg1MzMzODA0LCJuYmYiOjE3ODUzMzM4MDQsImV4cCI6MTc4NzkyNTgwNCwic3ViIjoiIiwic2NvcGVzIjpbImJhc2ljIl19.nDhTnCwbdR4bzP1SJntCfEs2oePEmhFdv7EIymqU18k8RO44rWZtmn2xd3l80aw_ju7AlYfrzIufEfJzLgAaZ2Wz8ZvNeHZ6wP1OeyYg4P0M0llv33XZYzibpQnVv8vm1sgwpMAaUvEQjd7lLXw9IFfUd9HN8sF6SXtyktIfKqCChgUFtS49yRoc7bY6bLJ5kydqU_ebDXir9epdSHp6ZaiOkOayG32K_5hAVDBrr2jNK0I5kGdklxA8p-Rh2mJeAJk5U1Vq7GMG30JgIT3jfkXDq34B3aEq67GPk60bF3F00fGcOjyRDyO-FoG7CjOxwSAn4wsL7ZIfYg9HEju8bQ";

  console.log(position, center, "positiongreg");

  return (
    <>
      {/* <MapContainer
        // center={position ? [position.lat, position.lng] : [center.lat, center.lng]}
        // zoom={minimize ? 12 : zoom}
        // zoomControl={false}
        // zoomSnap={0}
        // zoomDelta={0.5}
        // style={{ width: "100%", height: "100%" }}
        // dragging={dragging}
        // scrollWheelZoom={!minimize}
        center={[35.5, 51]}
        zoom={10}
        scrollWheelZoom={false}
        style={{ width: "100%", height: "100%", zIndex: 1 }} // <--- این خط را اضافه کن
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {children}
        <MapHandler zoom={zoom} />
      </MapContainer> */}
      <MapContainer
        center={[35.7219, 51.3347]} // مرکز تهران
        zoom={10}
        scrollWheelZoom={false}
        preferCanvas={true}
        style={{ width: "100%", height: "100%", zIndex: 1 }}
      >
        {/* ۲. کامپوننت اختصاصی مپ‌آی‌آر رو اینجا قرار بده */}
        <MapirLayer url="https://map.ir/shiveh" apiKey={MAPIR_TOKEN} />

        {children}
        <MapHandler zoom={zoom} />
      </MapContainer>
    </>
  );
}
